import { useState } from 'react';
import type { Task } from '../../../types';
import { AppIcon } from '../../../components/icons/AppIcon';
import { type TaskBucket } from '../lib/taskBuckets';
import { TaskCard } from './TaskCard';
import { cn } from '@/lib/utils';

interface Group {
  bucket: TaskBucket;
  tasks: Task[];
}

interface TaskGroupedListProps {
  groups: Group[];
  isLoading?: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleCompletion: (id: string) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onDuplicate: (task: Task) => void;
}

const META: Record<TaskBucket, { label: string; labelClass: string; countClass: string }> = {
  overdue: { label: 'Atrasadas', labelClass: 'text-error', countClass: 'bg-danger-bg text-danger-text' },
  today: { label: 'Hoje', labelClass: 'text-warning', countClass: 'bg-warning-bg text-warning-text' },
  week: { label: 'Esta semana', labelClass: 'text-primary', countClass: 'bg-primary-subtle text-primary' },
  later: { label: 'Próximas', labelClass: 'text-secondary', countClass: 'bg-surface-subtle text-secondary' },
  done: { label: 'Concluídas', labelClass: 'text-muted', countClass: 'bg-surface-subtle text-muted' },
};

export function TaskGroupedList({
  groups,
  isLoading,
  selectedIds,
  onToggleSelect,
  onToggleCompletion,
  onEdit,
  onDelete,
  onDuplicate,
}: TaskGroupedListProps) {
  const [collapsed, setCollapsed] = useState<Set<TaskBucket>>(new Set());

  const toggle = (b: TaskBucket) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  };

  if (isLoading) {
    return <ListStatus>Carregando tarefas…</ListStatus>;
  }

  if (groups.length === 0) {
    return <ListStatus>Nenhuma tarefa encontrada.</ListStatus>;
  }

  return (
    <div className="flex flex-col gap-s5">
      {groups.map(({ bucket, tasks }) => {
        const meta = META[bucket];
        const isCol = collapsed.has(bucket);
        const headingId = `task-group-${bucket}`;
        const listId = `task-group-list-${bucket}`;
        return (
          <section key={bucket} aria-labelledby={headingId}>
            <header className="mb-s2">
              <h2 id={headingId} className="m-0 text-xs font-semibold leading-none">
                <button
                  type="button"
                  onClick={() => toggle(bucket)}
                  className="flex w-full min-w-0 items-center gap-s2 text-left text-inherit font-inherit"
                  aria-expanded={!isCol}
                  aria-controls={listId}
                >
                  <span className={cn('uppercase', meta.labelClass)}>{meta.label}</span>
                  <span
                    className={cn(
                      'rounded-full px-s2 py-px text-xs font-semibold tabular-nums',
                      meta.countClass,
                    )}
                  >
                    {tasks.length}
                  </span>
                  <span className="h-px flex-1 bg-border" aria-hidden />
                  <AppIcon
                    name="chevron-down"
                    className={cn(
                      'size-4 shrink-0 text-muted transition-transform motion-reduce:transition-none',
                      isCol && '-rotate-90',
                    )}
                    aria-hidden
                  />
                </button>
              </h2>
            </header>
            {!isCol && (
              <ul id={listId} className="flex flex-col gap-s2">
                {tasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    selected={selectedIds.has(task.id)}
                    onToggleSelect={onToggleSelect}
                    onToggleCompletion={onToggleCompletion}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ListStatus({ children }: { children: string }) {
  return (
    <p
      role="status"
      className="rounded-lg border border-dashed border-border bg-card px-s5 py-s5 text-center text-sm text-muted"
    >
      {children}
    </p>
  );
}
